import clsx from "clsx";
import { differenceInSeconds } from "date-fns";

import { getSelectedTools } from "../tools";
import type { ToolType } from "../tools/types";

import { DEFAULT_MODEL_FOR_ASSESSMENT_QUERY, DEFAULT_MODEL_FOR_CHAT } from "./AISettings";

import { type AIContextPart, AIModelType, type AIServerRoleType } from "components/server-types";
import { HorizontalDivider } from "mds/components/HorizontalDivider";
import { showConfirmationModal } from "mds/components/Modal";
import { Tooltip } from "mds/components/Tooltip";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { useHoverable } from "mds/hooks/use-hoverable";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  CircleCheckIcon,
  CircleExclamationIcon,
  LoadingDotsIcon,
  RefreshIcon,
} from "mds/icons";
import { useAppDispatch, useAppSelector } from "store/index";
import { selectCurrentUserIsMPAdmin, selectUserSelectedAIModel } from "store/selectors";
import {
  selectIsAiDebuggerVisible,
  selectLastAiContextParts,
  selectToolNames,
} from "store/selectors/ai";
import { AIToolMessage, AIToolResponseMessage, aiStateActions } from "store/slices/ai";
import { isAiDebuggerVisible } from "utils/waffle-flags";

// This is input pricing. Output pricing is usually 4x.
const PRICE_PER_MODEL: Record<AIModelType, number> = {
  "gpt-4.1-nano": 0.1,
  "gpt-4.1": 2.0,
  "gpt-5-nano": 0.05,
  "gpt-5-mini": 0.25,
  "gpt-5": 1.25,
  "claude-3-5-haiku-latest": 0.8,
  "claude-sonnet-4-0": 3.0,
  "claude-opus-4-1": 15.0,
  "o4-mini": 1.1,
};
// We only output ~10% as much as we input, so we use a multiplier on the input
// tokens to estimate pricing for the total request.
const AVERAGE_OUTPUT_TO_INPUT_RATIO = 1.2;

// As of Sep 2025, average chars per token for english chat requests is 4
const AVERAGE_CHARS_PER_TOKEN = 4;

// As of Sep 2025, windows are 200k-400k normally, and we use the lower bound
const MIN_TOKEN_WINDOW = 200_000;

const contextPartLabel = (part: AIContextPart, index: number) => part.key ?? `context-${index}`;

const contextPartRowKey = (part: AIContextPart, index: number) => {
  if (part.kind === "gitbook") {
    return "gitbook-rag";
  }
  if (part.kind === "file") {
    return `file:${part.metadata.name}:${part.metadata.bytes}`;
  }
  return part.key ?? `system-${index}`;
};

const contextPartRole = (part: AIContextPart): AIServerRoleType => {
  if (part.kind === "gitbook") {
    return "assistant";
  }
  if (part.kind === "file") {
    return "user";
  }
  return "system";
};

/** Approximate size for cost line: system/gitbook use char count; files use raw bytes (multimodal is coarser). */
const totalDebuggerContextSize = (parts: AIContextPart[]) =>
  parts.reduce((sum, part) => {
    if (part.kind === "gitbook") {
      return sum + part.metadata.total_chars;
    }
    if (part.kind === "file") {
      return sum + part.metadata.bytes;
    }
    return sum + part.content.length;
  }, 0);

/** Tooltip / pre: JSON with long URLs must wrap instead of overflowing the bubble or modal. */
const AI_DEBUG_METADATA_TEXT_CLASS =
  "max-w-full whitespace-pre-wrap break-all [overflow-wrap:anywhere]";

const estimateRequestCost = (requestSize: number, model: AIModelType) => {
  // This functionality mainly serves to remind developers that AI is really cheap and we
  // should not be stingy with the context we provide.
  const tokens = Math.ceil(requestSize / AVERAGE_CHARS_PER_TOKEN);
  const contextPercent = Math.round((tokens / MIN_TOKEN_WINDOW) * 100);
  const price = (tokens / 1_000_000) * PRICE_PER_MODEL[model] * AVERAGE_OUTPUT_TO_INPUT_RATIO;
  const priceString = `~$${price.toFixed(5)}`;
  return { tokens, contextPercent, price: priceString };
};

export const AIAssistantDebugger = () => {
  const dispatch = useAppDispatch();
  const expanded = useAppSelector(selectIsAiDebuggerVisible);

  const icon = expanded ? <ChevronUpIcon /> : <ChevronDownIcon />;
  const aiModel = useAppSelector(selectUserSelectedAIModel) || DEFAULT_MODEL_FOR_CHAT;

  const lastAiContextParts = useAppSelector(selectLastAiContextParts);
  const toolNames = useAppSelector(selectToolNames);
  const tools = getSelectedTools(toolNames);

  const contextParts = lastAiContextParts?.parts ?? [];
  const totalSize = totalDebuggerContextSize(contextParts);
  const { tokens, contextPercent, price } = estimateRequestCost(totalSize, aiModel);
  const assessmentPrice = estimateRequestCost(totalSize, DEFAULT_MODEL_FOR_ASSESSMENT_QUERY).price;
  const assessmentModel = DEFAULT_MODEL_FOR_ASSESSMENT_QUERY;

  const openAllContextModal = () => {
    showConfirmationModal({
      title: "AI Context",
      hideCancelConfirmButtons: true,
      className: "!w-[80vw] !max-w-[80vw]",
      children: (
        <div className="max-h-[70vh] overflow-y-auto px-1">
          {contextParts.map((part, i) => {
            const body =
              part.kind === "gitbook" || part.kind === "file"
                ? JSON.stringify(part.metadata, null, 2)
                : part.content;
            return (
              <div
                className="border-neutral-200 border-t py-2 first:border-t-0 first:pt-0"
                key={contextPartRowKey(part, i)}
              >
                <div className="mb-1 text-sm font-medium">
                  {contextPartLabel(part, i)} ({contextPartRole(part)})
                </div>
                <pre
                  aria-label={i === 0 ? "ai-context-modal-content" : undefined}
                  className={`text-xs ${AI_DEBUG_METADATA_TEXT_CLASS}`}
                >
                  {body}
                </pre>
              </div>
            );
          })}
          <HorizontalDivider />
          <h4 className="pt-4">Available Tools</h4>
          {tools.length > 0 ? (
            <div className="flex flex-col items-start gap-1 rounded-md">
              {tools.map((tool) => (
                <div key={tool.name}>
                  <div className="font-bold">{tool.name}</div>
                  <div className="text-sm">{tool.description}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-start gap-1 rounded-md">
              <div className="py-1 text-sm">None</div>
            </div>
          )}
        </div>
      ),
    });
  };

  return (
    <div className="rounded-md bg-yellow-tint-90 p-2 text-sm">
      <h4>
        <button
          className="flex w-full items-center justify-between"
          type="button"
          onClick={() => dispatch(aiStateActions.setAiDebuggerVisible(!expanded))}
        >
          <span>AI Assistant Debugger</span>
          <span className="mr-2">{icon}</span>
        </button>
      </h4>
      {expanded && (
        <div className="mt-2 max-h-[min(50vh,28rem)] overflow-y-auto overscroll-y-contain pt-2">
          <div className="flex flex-col gap-2">
            <h4>Base Context</h4>
            <div className="flex flex-col items-start gap-1 rounded-md px-4">
              <button
                className="w-full cursor-pointer text-left"
                type="button"
                onClick={openAllContextModal}
              >
                {contextParts.length === 0 && (
                  <div className="py-1 text-sm">
                    {lastAiContextParts === null
                      ? "Context will appear here after you send a message."
                      : "None"}
                  </div>
                )}
                {contextParts.map((part, i) => (
                  <AIContextInfoItem index={i} key={contextPartRowKey(part, i)} part={part} />
                ))}
              </button>
            </div>
            <h4>Available Tools</h4>
            {tools.length > 0 ? (
              <button
                className="w-full cursor-pointer text-left"
                type="button"
                onClick={openAllContextModal}
              >
                <div className="flex flex-col items-start gap-1 rounded-md px-4">
                  {tools.map((tool) => (
                    <AIToolCallItem key={tool.name} tool={tool} />
                  ))}
                </div>
              </button>
            ) : (
              <div className="flex flex-col items-start gap-1 rounded-md px-4">
                <div className="py-1 text-sm">None</div>
              </div>
            )}
            <h4>Model info</h4>
            <div className="flex flex-col items-start gap-1 rounded-md px-4">
              <div className="w-full py-1">Model: {aiModel}</div>
              <div className="w-full py-1">
                Tokens: {tokens}/{MIN_TOKEN_WINDOW} ({contextPercent}%)
              </div>
              <Tooltip
                content={`This is an estimate of the cost of the request if send through chat with the currently selected model (${aiModel}). For AI actions like assessment, we use ${assessmentModel}, which would cost ${assessmentPrice} for the same context size.`}
              >
                <div className="w-full py-1">Cost: {price}</div>
              </Tooltip>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const AIContextInfoItem = ({ part, index }: { part: AIContextPart; index: number }) => {
  const id = contextPartLabel(part, index);
  const role = contextPartRole(part);
  const contextSize =
    part.kind === "gitbook"
      ? part.metadata.total_chars
      : part.kind === "file"
        ? part.metadata.bytes
        : part.content.length;
  const kB = contextSize / 1024;
  const tooltip =
    part.kind === "gitbook" || part.kind === "file"
      ? JSON.stringify(part.metadata, null, 2)
      : part.content;
  const isMetadataPart = part.kind === "gitbook" || part.kind === "file";
  return (
    <div className="w-full py-1 text-sm">
      <Tooltip
        content={tooltip}
        contentClassName={
          isMetadataPart
            ? clsx("!max-w-[min(480px,90vw)]", AI_DEBUG_METADATA_TEXT_CLASS)
            : undefined
        }
        wrapperClassName="w-full min-w-0"
      >
        <div className="flex w-full min-w-0 items-center justify-between gap-1">
          <div className="min-w-0 truncate">
            {id} ({role})
          </div>
          <div className="shrink-0">{kB <= 1 ? kB.toFixed(1) : Math.round(kB)} kb</div>
        </div>
      </Tooltip>
    </div>
  );
};

export const AIToolCallItem = ({ tool }: { tool: ToolType }) => {
  return (
    <Tooltip content={tool.description} wrapperClassName="w-full">
      <div className="w-full py-1 text-sm">{tool.name}</div>
    </Tooltip>
  );
};

export const AIToolCallOutputItem = ({
  functionCall,
  functionCallOutput,
}: {
  functionCall: AIToolMessage;
  functionCallOutput?: AIToolResponseMessage;
}) => {
  const { isHovering, hoverParentProps } = useHoverable();
  const isMPadmin = useAppSelector(selectCurrentUserIsMPAdmin);
  const canSeeAIDebugger = isAiDebuggerVisible();
  const finished = functionCallOutput?.finishedAt || functionCallOutput?.error;
  const failed = functionCallOutput?.error;
  const toolNames = useAppSelector(selectToolNames);
  const tools = getSelectedTools(toolNames);
  const duration = functionCallOutput?.finishedAt
    ? differenceInSeconds(functionCallOutput.finishedAt, functionCall.createdAt)
    : null;
  const toolArgs = functionCall.arguments;
  const toolOutputContext = functionCallOutput?.output;
  const tooltipContent = [
    failed ? ["Error:", JSON.stringify(functionCallOutput.error, null, 2)].join("\n") : null,
    ["Tool called with:", JSON.stringify(toolArgs, null, 2)].join("\n"),
    [
      "Tool output:",
      JSON.stringify(toolOutputContext, null, 2)?.slice(0, 200),
      "(...truncated to 200 characters)",
    ].join("\n"),
  ]
    .filter(Boolean)
    .join("\n\n");

  const retryToolCall = () => {
    const tool = tools.find((t) => t.name === functionCall.name);
    if (!tool) {
      alert(`Tool ${functionCall.name} not found`);
      return;
    }

    tool.callback(toolArgs);
  };

  const Message = (
    <div
      className="relative m-2 flex w-full items-center justify-start gap-2 text-sm text-black-tint-40"
      {...(canSeeAIDebugger ? hoverParentProps : {})}
    >
      {failed ? <CircleExclamationIcon /> : finished ? <CircleCheckIcon /> : <LoadingDotsIcon />}
      <span>
        {failed
          ? `Action failed: ${JSON.stringify(functionCallOutput.error)}`
          : functionCall.status}
      </span>
      {typeof duration === "number" && !isNaN(duration) && duration !== 0 && (
        <span className="ml-4">({duration}s)</span>
      )}
      {isMPadmin && isHovering && (
        <SquareButton
          className="absolute right-0"
          kind="tertiary"
          size="xs"
          onClick={() => retryToolCall()}
        >
          <RefreshIcon /> <span>Re-apply</span>
        </SquareButton>
      )}
    </div>
  );

  if (canSeeAIDebugger) {
    return (
      <Tooltip
        {...(canSeeAIDebugger ? hoverParentProps : {})}
        content={tooltipContent}
        key={functionCall.id}
        wrapperClassName="w-full"
      >
        {Message}
      </Tooltip>
    );
  }
  return Message;
};
